import axios from 'axios'
import Vue from 'vue'
import helpers from '@/utils/helpers'
import VueCookies from 'vue-cookies'
import {store} from '@/integrations/store'
import {eventBus} from '@/utils/eventBus'
import {markOffline, markOnline} from '@/utils/offline'

const isNetworkError = error => !error.response && (error.code === 'ECONNABORTED' || error.message === 'Network Error' || !navigator.onLine || error.request)

// The service worker tags cache-fallback responses with this header.
// Such responses mean the network request failed — we are offline.
const fromSwCache = response => !!(response && response.headers && response.headers['x-sw-cache'])
const KEY = document.querySelector('meta[name="app-key"]').content
const API_URL = document.querySelector('meta[name="app-url"]').content

Vue.use(VueCookies)

export const API = axios.create({
  baseURL: API_URL,
  headers: {
    'app-key': KEY
  }
})

const AXIOS = axios.create({
  baseURL: API_URL,
  headers: {
    'access-token': helpers.isPreviewFrame() ? store.getters['user/userTokenAdmin'] : store.getters['user/userTokenAccess']
  }
})

API.interceptors.response.use(
  response => {
    if (fromSwCache(response)) markOffline()
    else markOnline()
    return response
  },
  error => {
    if (isNetworkError(error)) {
      markOffline()
      return Promise.reject(error)
    }
    markOnline()
    switch (error.response.status) {
      case 422:
        error.response.data.forEach(error => {
          if (error.field === '_popup') {
            return eventBus.$emit('popup', error.message)
          }
        })
        break
    }

    return Promise.reject(error)
  }
)

AXIOS.interceptors.response.use(
  response => {
    if (fromSwCache(response)) {
      // Cache fallback while offline. Skip the core-ver check: the cached
      // header is stale and the mismatch would wipe every cache and reload
      // into a blank page with no network to recover from.
      markOffline()
      return response
    }
    markOnline()
    const CORE_VER = Vue.$cookies.get('core_ver')
    if (!CORE_VER && response.headers['x-core-ver']) Vue.$cookies.set('core_ver', response.headers['x-core-ver'])
    if (CORE_VER && response.headers['x-core-ver'] && String(CORE_VER) !== String(response.headers['x-core-ver'])) {
      Vue.$cookies.set('core_ver', response.headers['x-core-ver'])
      caches.keys().then(function (cacheNames) {
        // Backend bumped: drop runtime caches (stale API data) but NEVER the
        // workbox-managed precache ('workbox-*' or '<cacheId>-precache-*',
        // e.g. 'tt-next-precache-v2-…'). It is populated only during SW
        // install — deleting it leaves the active SW permanently cacheless
        // and kills offline mode until the next frontend deploy. Fresh app
        // code is delivered by the SW update cycle, not by this wipe.
        for (let name of cacheNames) {
          const workboxManaged = name.startsWith('workbox-') || name.includes('-precache-') || name.includes('-runtime-')
          if (!workboxManaged) caches.delete(name)
        }
        let timeout = (Math.floor(Math.random() * (20 - 1)) + 1) * 100 // 100...2000 ms step 100
        setTimeout(() => {
          window.location.reload(true)
        }, timeout)
      })
    }
    return response
  },
  error => {
    if (isNetworkError(error)) {
      markOffline()
      return Promise.reject(error)
    }
    markOnline()
    switch (error.response.status) {
      case 401:
        store.commit('user/userLogOut')
        window.location.reload()
        break
      case 404:
        eventBus.$emit('404')
        break
      case 406:
        eventBus.$emit('goToRootModule')
        break
      case 422:
        error.response.data.forEach(error => {
          if (error.field === '_popup') {
            return eventBus.$emit('popup', error.message)
          }
        })

        break
      case 451:
        store.commit('setAppLockedState', true)
        store.commit('setAppState', false)
        break
    }
    if (error.response.status === 406) return
    return Promise.reject(error)
  }
)

export default AXIOS
